import { Divider, Label } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import {
  IPhotoAndNotesCounts,
  PHOTO_AND_NOTES_COUNTS_QUERY,
  toCreatedOnBound,
} from 'containers/dashboard/dashboard-grid/card/PhotoAndNotesDashboardCard/photos-and-notes-dashboard-card-query';
import usePhotosViewCollectionPermissionEnabled from 'containers/photos/hooks/usPhotosViewCollectionPermissionEnabled/usePhotosViewCollectionPermissionEnabled';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { DateTime } from 'luxon';
import { ClassName } from 'types/ClassName';
import ITimeRange from 'types/TimeRange';
import { t } from 'utils/localize';
import './DailyPhotoAndNotesDashboardCardFooter.scss';

export interface IDailyPhotoAndNotesDashboardCardFooterProps {
  className?: ClassName;
  timeRange: ITimeRange<DateTime>;
  memberId?: string;
  projectId?: string;
}

const DailyPhotoAndNotesDashboardCardFooter = (props: IDailyPhotoAndNotesDashboardCardFooterProps) => {
  const { className, timeRange, memberId, projectId } = props;

  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();
  const requirePermission = usePhotosViewCollectionPermissionEnabled();

  const startTime = toCreatedOnBound(timeRange.startTime);
  const endTime = toCreatedOnBound(timeRange.endTime);

  const { data } = useQuery({
    queryKey: [...baseKey, 'photos-and-notes-footer', { startTime, endTime, memberId, projectId, requirePermission }],
    queryFn: async () => {
      const result = await graphQLClient.request<{ dashboard: { photoAndNotesCounts: IPhotoAndNotesCounts } }>({
        document: PHOTO_AND_NOTES_COUNTS_QUERY,
        variables: {
          ranges: [{ startTime, endTime }],
          memberId: memberId ?? null,
          projectId: projectId ?? null,
          requirePermission,
          includeFooter: true,
        },
      });
      const counts = result.dashboard.photoAndNotesCounts;
      return { photoCount: counts.photoCount, noteCount: counts.noteCount };
    },
  });

  const photoCount = data?.photoCount ?? 0;
  const noteCount = data?.noteCount ?? 0;

  const classes = classNames('daily-photo-and-notes-dashboard-card-footer', className);

  return (
    <div className={classes}>
      <Divider className={'mb-4 footer-divider'} />
      <Label className={'title-label'}>
        {t('Photos') + ': ' + photoCount + ' | ' + t('Notes') + ': ' + noteCount}
      </Label>
    </div>
  );
};

export default DailyPhotoAndNotesDashboardCardFooter;
